import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  StreamErrorFrameError,
  streamAgent,
  type AgentFrame,
} from 'src/data/api/streamAgent';
import { reportError } from '../../../shared/errors';
import {
  INITIAL_ASTRA_STATE,
  appendUserMessage,
  applyAgentFrame,
  createSessionId,
  createTurnMessageIds,
  describeStreamError,
  markStoppedByUser,
  markStreamFailed,
  settleAgentTurn,
  type AstraState,
} from './conversationState';

export type { AstraMessage, AstraState } from './conversationState';

/** Logical agent selector resolved to a runtime by the Starlight target registry. */
const ASTRA_AGENT_ID = 'astra';

/** The runtime's own diagnostic from an in-stream failure frame, for the error report. */
function streamErrorDetail(err: unknown): Record<string, string> {
  if (!(err instanceof StreamErrorFrameError)) return {};
  return { detail: err.detail, ...(err.errorType ? { errorType: err.errorType } : {}) };
}

/** The stream completed with HTTP 200 but never delivered any answer text. */
class EmptyAnswerError extends Error {
  constructor() {
    super('Agent stream closed without any answer text');
    this.name = 'EmptyAnswerError';
  }
}

/** Only answer text counts as output the user can read; reasoning, tool and done frames don't. */
function isAnswerText(frame: AgentFrame): boolean {
  return frame.type === 'token' && frame.text.length > 0;
}

export interface AstraConversation {
  state: AstraState;
  userId: string;
  /**
   * True for the full duration of a turn — from submit until the stream ends
   * (success, error, or timeout). Covers both the pre-first-frame "loading" phase
   * and the active streaming phase, so callers can block a new submission until the
   * current turn finishes and avoid concurrent streams on the same session.
   */
  isResponding: boolean;
  sendMessage: (content: string) => Promise<void>;
  /**
   * Cancel the in-flight turn (if any) without clearing the conversation. Any answer
   * text already streamed is kept and flagged as interrupted; if nothing had streamed
   * yet the turn simply ends with no error. No-op when idle.
   */
  stopResponding: () => void;
  startNewConversation: () => void;
  togglePanel: () => void;
}

const AstraContext = createContext<AstraConversation | null>(null);

export function useAstra(): AstraConversation {
  const context = useContext(AstraContext);
  if (!context) {
    throw new Error('useAstra must be used within an AstraProvider');
  }
  return context;
}

export interface AstraProviderProps {
  children: ReactNode;
  businessUnitId: string;
  isAuthenticated: boolean;
  userId: string;
}

/**
 * Owns the Astra conversation: the message list, the in-flight turn and the
 * Starlight session id. UI-agnostic — the legacy Cloudscape copilot, the
 * Stencil desktop panel and the mobile `/chat` page all read it through
 * `useAstra()`.
 */
export function AstraProvider({
  children,
  businessUnitId,
  isAuthenticated,
  userId,
}: AstraProviderProps): React.JSX.Element {
  const [state, setState] = useState<AstraState>(INITIAL_ASTRA_STATE);

  // Guards against starting a second turn while one is in flight. A ref (not state) so
  // the check is synchronous and race-safe regardless of the caller/entry path, and
  // immune to the stale state a memoized sendMessage closure would otherwise see.
  const activeTurnRef = useRef(false);

  // Bumped whenever the conversation is reset. A turn captures the generation at start;
  // any of its late frames or its final settle are dropped once the generation moves on,
  // so a `startNewConversation` mid-stream can't repopulate the cleared conversation.
  const generationRef = useRef(0);
  // Abort handle for the in-flight stream, so a reset also tears down the network request.
  const abortRef = useRef<AbortController | null>(null);

  const togglePanel = useCallback((): void => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const startNewConversation = useCallback((): void => {
    // Supersede any in-flight turn: bump the generation so its late frames/settle are
    // ignored, abort its network stream, and release the send guard for a fresh turn.
    generationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    activeTurnRef.current = false;
    setState((prev) => ({ ...prev, ...INITIAL_ASTRA_STATE, isOpen: prev.isOpen }));
  }, []);

  const sendMessage = useCallback(
    async (content: string): Promise<void> => {
      // Ignore empty input, unauthenticated callers, and — critically — a submit while a
      // turn is already streaming, so we never open a second concurrent stream on the
      // same session.
      const prompt = content.trim();
      if (!isAuthenticated || !prompt || activeTurnRef.current) return;
      activeTurnRef.current = true;

      // Snapshot this turn's generation and open an abort handle. If the conversation is
      // reset mid-stream, the generation moves on (so late updates below are dropped) and
      // the stream is aborted.
      const generation = generationRef.current;
      const isCurrentTurn = (): boolean => generationRef.current === generation;
      const controller = new AbortController();
      abortRef.current = controller;

      const timestamp = Date.now();
      const { user: userMsgId, agent: agentMsgId } = createTurnMessageIds(timestamp);
      // Reuse the conversation's session id across turns; mint one on the first turn.
      const sessionId = state.sessionId ?? createSessionId();

      setState((prev) =>
        appendUserMessage(
          prev,
          { id: userMsgId, role: 'user', content: prompt, timestamp },
          sessionId,
        ),
      );

      // Drop frames from a turn the user has already moved on from (new conversation).
      let receivedAnswerText = false;
      const onFrame = (frame: AgentFrame): void => {
        receivedAnswerText ||= isAnswerText(frame);
        if (isCurrentTurn()) setState((prev) => applyAgentFrame(prev, agentMsgId, frame));
      };

      try {
        await streamAgent({
          agentId: ASTRA_AGENT_ID,
          prompt,
          businessUnitId,
          sessionId,
          onFrame,
          signal: controller.signal,
        });
        // A stream that closes cleanly without any answer text (no frames, or only
        // reasoning/tool/done frames) is not an answer — the user would see nothing to
        // read. Treat it as a failure so it's visible and logged.
        if (!receivedAnswerText) throw new EmptyAnswerError();
        // A reset during the stream supersedes this turn; don't touch the new conversation.
        if (isCurrentTurn()) setState((prev) => settleAgentTurn(prev, agentMsgId));
      } catch (err) {
        // Superseded/aborted by a reset — the new conversation owns the state now.
        if (!isCurrentTurn()) return;
        // This turn's controller was aborted by the user (not a reset).
        if (controller.signal.aborted) {
          setState((prev) => markStoppedByUser(prev, agentMsgId));
          return;
        }
        reportError(err, {
          source: 'astra.stream',
          sessionId,
          ...streamErrorDetail(err),
        });
        const errorMessage = describeStreamError(err);
        setState((prev) => markStreamFailed(prev, agentMsgId, errorMessage));
      } finally {
        // Release the guards only if this turn is still current — a reset (or a newer
        // turn) has already re-armed them and must not be clobbered by a stale settle.
        if (isCurrentTurn()) {
          activeTurnRef.current = false;
          abortRef.current = null;
        }
      }
    },
    [isAuthenticated, state.sessionId, businessUnitId],
  );

  // Cancel the current turn without clearing the conversation. Aborting the stream lands
  // in sendMessage's catch, which (seeing controller.signal.aborted) keeps any partial
  // answer and clears the busy state. Does NOT bump the generation, so this turn still
  // owns the state and settles itself. No-op when idle.
  const stopResponding = useCallback((): void => {
    abortRef.current?.abort();
  }, []);

  // Busy for the whole turn: the pre-first-frame loading phase or any streaming message.
  const isResponding =
    state.isLoading || state.messages.some((message) => message.isStreaming);

  const value = useMemo<AstraConversation>(
    () => ({
      state,
      userId,
      isResponding,
      sendMessage,
      stopResponding,
      startNewConversation,
      togglePanel,
    }),
    [
      state,
      userId,
      isResponding,
      sendMessage,
      stopResponding,
      startNewConversation,
      togglePanel,
    ],
  );

  return <AstraContext.Provider value={value}>{children}</AstraContext.Provider>;
}
